import AddAgency from '../components/AddAgency';
import { type Agency } from '../../../common/types';
import ApproveAgency from '../components/ApproveAgency';
import Breadcrumb from '../../../components/Breadcrumb';
import DeleteAgency from '../components/DeleteAgency';
import EditAgency from '../components/EditAgency';
import { Link } from 'react-router-dom';
import ListAgencies from '../components/ListAgencies';
import LoadingSpinner from '../../../components/LoadingSpinner';
import Pagination from '../../../components/Pagination';
import SuccessMessage from '../../../components/success-message';
import { useGetAgenciesQuery } from '../agency.api-slice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface IProps {
  filter?: { pending: boolean };
}

const ListAgenciesPage: React.FC<IProps> = ({ filter }) => {
  const [editingItem, setEditingItem] = useState<Agency | null>(null);
  const [deletingItem, setDeletingItem] = useState<Agency | null>(null);
  const [approvingItem, setApprovingItem] = useState<Agency | null>(null);
  const [openAddModal, setOpenAddModal] = useState<boolean>(false);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const { data: items, isLoading } = useGetAgenciesQuery({ page, pendingOnly: filter?.pending === true });
  const { t } = useTranslation();

  const toggleAddModal = (): void => {
    setOpenAddModal(!openAddModal);
  };

  const toggleEditModal = (): void => {
    if (openEditModal) {
      setEditingItem(null);
    }
    setOpenEditModal(!openEditModal);
  };

  const toggleDeleteModal = (): void => {
    if (openDeleteModal) {
      setDeletingItem(null);
    }
    setOpenDeleteModal(!openDeleteModal);
  };

  const toggleApproveModal = (): void => {
    if (openApproveModal) {
      setApprovingItem(null);
    }
    setOpenApproveModal(!openApproveModal);
  };

  const handleEditingItem = (item: Agency | null): void => {
    setEditingItem(item);
  };

  const handleDeletingItem = (item: Agency | null): void => {
    setDeletingItem(item);
  };

  const handleApprovingItem = (item: Agency | null): void => {
    setApprovingItem(item);
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <div className="pb-4">
        <h1>{t('agencies.list.title')}</h1>
        <Breadcrumb
          items={[{ label: t('common.adminBreadcrumbLabel') }, { label: t('agencies.list.breadcrumbLabel') }]}
        />
      </div>

      <div className="w-full flex flex-col md:flex-row md:justify-between md:items-center space-y-2">
        <h1 className="text-2xl">{t('agencies.list.heading')}</h1>
        <div className="flex justify-items-center md:justify-between md:items-center gap-x-2">
          {filter?.pending === true && (
            <Link to="/admin/agencies">
              <button data-cy="list-agency-btn" className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">
                {t('agencies.list.listLinkLabel')}
              </button>
            </Link>
          )}
          {filter?.pending !== true && (
            <>
              <Link to="/admin/agencies/pending-requests">
                <button data-cy="pending-agency-btn" className="text-gray ml-4 px-5 py-2 rounded-lg bg-gray-50">
                  {t('agencies.list.pendingListLabel')}
                </button>
              </Link>
              <button
                data-cy="add-agency-btn"
                onClick={toggleAddModal}
                className="text-white ml-4 px-5 py-2 rounded-lg bg-[#1D8A43]"
              >
                {t('agencies.list.inviteLabel')}
              </button>
            </>
          )}
        </div>
      </div>

      <AddAgency isOpen={openAddModal} toggleModal={toggleAddModal} />

      <EditAgency isOpen={openEditModal} toggleEditModal={toggleEditModal} item={editingItem} />

      <DeleteAgency isOpen={openDeleteModal} toggleDeleteModal={toggleDeleteModal} item={deletingItem} />

      <ApproveAgency isOpen={openApproveModal} toggleApproveModal={toggleApproveModal} item={approvingItem} />

      {items?.data == null ||
        (items.data.length === 0 && <SuccessMessage message={t('agencies.list.empty')} type="error" />)}

      {items?.data != null && items.data.length > 0 && (
        <>
          <ListAgencies
            items={items.data}
            filter={{ pending: filter?.pending === true }}
            toggleEditModal={toggleEditModal}
            toggleDeleteModal={toggleDeleteModal}
            toggleApproveModal={toggleApproveModal}
            handleEditingItem={handleEditingItem}
            handleDeletingItem={handleDeletingItem}
            handleApprovingItem={handleApprovingItem}
          />
          <Pagination pageData={items.meta} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default ListAgenciesPage;
